<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import UiButton from './UiButton.vue'
import { useBackdropClose } from '@/lib/backdropClose'
import { useDialogsStore } from '@/stores/dialogs'

const dialogs = useDialogsStore()
const dlg = computed(() => dialogs.current)

/** prompt 的輸入值（批153）；每開一次對話框重設成 `initial`。 */
const text = ref('')
const inputEl = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)
watch(dlg, async (d) => {
  if (d?.kind !== 'prompt') return
  text.value = d.initial ?? ''
  await nextTick()
  inputEl.value?.focus()
})
const promptBlocked = computed(() => {
  const d = dlg.value
  return d?.kind === 'prompt' && !!d.required && !text.value.trim()
})

function ok() {
  const d = dlg.value
  if (!d) return
  if (d.kind === 'confirm') d.resolve(true)
  else if (d.kind === 'prompt') {
    if (promptBlocked.value) return
    d.resolve(text.value.trim())
  } else d.resolve()
  dialogs.close()
}
function cancel() {
  const d = dlg.value
  if (!d) return
  if (d.kind === 'confirm') d.resolve(false)
  else if (d.kind === 'prompt') d.resolve(null)
  else d.resolve()
  dialogs.close()
}
function onInputKey(e: KeyboardEvent) {
  // 單行：Enter 送出；多行：Ctrl／⌘＋Enter 送出（Enter 是換行）。
  const d = dlg.value
  if (d?.kind !== 'prompt' || e.key !== 'Enter' || e.isComposing) return
  if (d.multiline && !(e.ctrlKey || e.metaKey)) return
  e.preventDefault()
  ok()
}

// 同 `UiModal.vue`：框內選字拖到框外放開不該取消對話框（見 lib/backdropClose）。
const backdropClose = useBackdropClose(cancel)
</script>

<template>
  <div v-if="dlg" class="dlg-host" v-on="backdropClose">
    <div class="dlg-card">
      <h3 v-if="dlg.title">{{ dlg.title }}</h3>
      <p class="msg">{{ dlg.message }}</p>
      <label v-if="dlg.kind === 'prompt'" class="dlg-field">
        <span v-if="dlg.label" class="dlg-label">{{ dlg.label }}</span>
        <textarea
          v-if="dlg.multiline"
          ref="inputEl"
          v-model="text"
          rows="3"
          :placeholder="dlg.placeholder"
          :maxlength="dlg.maxLength"
          @keydown="onInputKey"
        />
        <input
          v-else
          ref="inputEl"
          v-model="text"
          :placeholder="dlg.placeholder"
          :maxlength="dlg.maxLength"
          @keydown="onInputKey"
        />
      </label>
      <footer>
        <UiButton v-if="dlg.kind !== 'notify'" @click="cancel">
          {{ dlg.cancelText ?? '取消' }}
        </UiButton>
        <UiButton
          :variant="dlg.kind !== 'notify' && dlg.danger ? 'danger' : 'primary'"
          :disabled="promptBlocked"
          @click="ok"
        >
          {{ dlg.kind !== 'notify' ? (dlg.confirmText ?? '確定') : '我知道了' }}
        </UiButton>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.dlg-host {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  display: grid;
  place-items: center;
  padding: var(--sp-6);
}
.dlg-card {
  width: min(420px, 100%);
  background: var(--bg-panel);
  border: 1px solid var(--border);
  border-radius: var(--r-xl);
  box-shadow: var(--shadow-lg);
  padding: var(--sp-5);
  animation: dlg-pop var(--dur-base) var(--ease);
}
@keyframes dlg-pop {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.dlg-card h3 {
  margin: 0 0 var(--sp-2);
  font-size: var(--fs-15);
}
.msg {
  margin: 0 0 var(--sp-4);
  color: var(--fg-2);
  font-size: var(--fs-13);
  line-height: var(--lh-base);
  white-space: pre-wrap;
}
.dlg-field {
  display: grid;
  gap: 4px;
  margin: 0 0 var(--sp-4);
}
.dlg-label {
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.dlg-field input,
.dlg-field textarea {
  width: 100%;
  box-sizing: border-box;
  font: inherit;
}
footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
}
</style>
